import { Button } from "@/components/ui/button";
import SiriusLogo from "@/components/SiriusLogo";
import { User, Bell, Settings, LogOut, Shield, MessageCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { routes } from "@/lib/security";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavBar = () => {
  const { user, userRole, signOut } = useAuth();
  const navigate = useNavigate();
  const [notificationsViewed, setNotificationsViewed] = useState(false);

  const userName = (user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Usuário').split(' ')[0];
  const userStatus = userRole === 'admin' ? 'Administrador' : 'Investidor';

  const handleSignOut = async () => {
    await signOut();
    navigate(routes.auth);
  };

  const handleNotificationsOpen = () => {
    setNotificationsViewed(true);
  };

  return (
    <header className="bg-card/80 backdrop-blur-sm border-b border-border shadow-soft sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4 cursor-pointer min-w-0" onClick={() => navigate(routes.main)}>
            <div className="p-1.5 sm:p-2.5 bg-gradient-primary rounded-xl shadow-glow flex-shrink-0">
              <SiriusLogo className="h-5 w-5 sm:h-7 sm:w-7 text-primary-foreground" />
            </div>
            <div className="min-w-0 hidden sm:block">
              <h1 className="text-lg sm:text-xl font-bold text-foreground">Sirius</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Portal do Investidor</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Notifications Dropdown */}
            <DropdownMenu onOpenChange={(open) => { if (open) handleNotificationsOpen(); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 sm:h-10 sm:w-10">
                  <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 sm:w-80 bg-card border-border mr-2 sm:mr-0">
                <DropdownMenuLabel>
                  Notificações
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Nenhuma notificação no momento
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Panel Access */}
            {userRole === 'admin' && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(routes.panel)}
                className="relative h-9 w-9 sm:h-10 sm:w-10"
              >
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </Button>
            )}

            {/* Settings Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 sm:w-56 bg-card border-border mr-2 sm:mr-0">
                <DropdownMenuLabel>Configurações</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(routes.user)} className="py-3">
                  <User className="mr-2 h-4 w-4" />
                  Meu Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open('https://wa.me/41772626216', '_blank')} className="py-3">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Suporte
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive py-3">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair da Conta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-border">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{userName}</p>
                <p className="text-xs text-muted-foreground">{userStatus}</p>
              </div>
              <div className="relative">
                <div className="h-10 w-10 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
                  {userRole === 'admin' ? (
                    <Shield className="h-5 w-5 text-primary-foreground" />
                  ) : (
                    <User className="h-5 w-5 text-primary-foreground" />
                  )}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-success rounded-full border-2 border-background" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;